import { Alert, Button, Form, Input } from "antd";
import type { InputRef } from "antd";
import type { MessageInstance } from "antd/es/message/interface";
import { useEffect, useRef, useState } from "react";
import {
  requestPasswordOtp,
  setPasswordWithOtp,
} from "../../../../apis/auth.api";
import { AuthError } from "../../../../apis/auth.errors";
import { establishSession } from "../../../../apis/session";
import { applyFieldErrors } from "../apply_field_errors";

const RESEND_COOLDOWN_SECONDS = 30;

interface Props {
  initialPhone: string;
  reason: "first_time" | "forgot" | null;
  messageApi: MessageInstance;
  onBackToLogin: (phone: string) => void;
}

const maskPhone = (phone: string) =>
  phone.length === 10 ? `••••••${phone.slice(-4)}` : phone;

const SetPasswordForm = ({
  initialPhone,
  reason,
  messageApi,
  onBackToLogin,
}: Props) => {
  const [step, setStep] = useState<"phone" | "verify">("phone");
  const [phone, setPhone] = useState(initialPhone);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [phoneForm] = Form.useForm<{ phone: string }>();
  const [verifyForm] = Form.useForm<{
    otp: string;
    password: string;
    confirm_password: string;
  }>();

  const phoneRef = useRef<InputRef>(null);
  const otpRef = useRef<InputRef>(null);

  useEffect(() => {
    if (step === "phone") phoneRef.current?.focus();
    else otpRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendCode = async (target: string) => {
    setSending(true);
    const res = await requestPasswordOtp(target);
    setSending(false);

    if (res.ok) {
      setPhone(target);
      setStep("verify");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      messageApi.success(`Code sent to ${maskPhone(target)}`);
      return;
    }

    const err = res as AuthError;
    if (err.code === "rate_limited") {
      // Stay on this step: no code was sent, so advancing would ask the user for
      // something that does not exist.
      setCooldown(err.retryAfter || RESEND_COOLDOWN_SECONDS);
    }
    const orphans = applyFieldErrors(phoneForm, err, ["phone"]);
    if (orphans.length) messageApi.error(orphans.join(" "));
    else if (!err.fieldErrors) messageApi.error(err.message);
  };

  const onSubmitPhone = ({ phone: value }: { phone: string }) =>
    sendCode((value ?? "").trim());

  const onSubmitPassword = async (values: {
    otp: string;
    password: string;
    confirm_password: string;
  }) => {
    setSubmitting(true);
    // confirm_password is a client-side check only and is never sent.
    const res = await setPasswordWithOtp(phone, values.otp, values.password);

    if (res.ok && res.token) {
      establishSession({ token: res.token, refresh: res.refresh });
      return; // navigating away — keep the spinner
    }
    setSubmitting(false);

    if (res.ok) {
      // Backend chose not to auto-login. Send them round to sign in.
      messageApi.success("Password set. Please log in.");
      onBackToLogin(phone);
      return;
    }

    const err = res as AuthError;
    const orphans = applyFieldErrors(verifyForm, err, [
      "otp",
      "password",
      "confirm_password",
    ]);

    if (err.code === "invalid_otp") {
      verifyForm.setFields([{ name: "otp", errors: [err.message] }]);
    } else if (orphans.length) {
      messageApi.error(orphans.join(" "));
    } else if (!err.fieldErrors) {
      messageApi.error(err.message);
    }
  };

  return (
    <>
      {reason === "first_time" && (
        <Alert
          type="info"
          showIcon
          className="mb-3 w-75"
          message="You haven't set a password yet"
          description="Get a one-time code on your phone to create one."
        />
      )}

      {/* The two steps MUST carry distinct keys. Without them React sees the
          same component type in the same position and reconciles them as one
          element, updating props instead of remounting — so rc-field-form keeps
          its original binding to phoneForm and `verifyForm` is never connected.
          onFinish still fires (it is just a prop), so the bug is invisible on
          the happy path and only shows up when something calls setFields on the
          unconnected instance, where it silently does nothing. */}
      {step === "phone" ? (
        <Form
          key="phone-step"
          form={phoneForm}
          layout="vertical"
          onFinish={onSubmitPhone}
          initialValues={{ phone: initialPhone }}
          disabled={sending}
          requiredMark={false}
          className="w-75"
        >
          <Form.Item
            name="phone"
            label="Phone number"
            rules={[
              {
                required: true,
                pattern: /^[6-9]\d{9}$/,
                message: "Enter a valid 10-digit mobile number",
              },
            ]}
          >
            <Input
              ref={phoneRef}
              inputMode="numeric"
              maxLength={10}
              autoComplete="username"
              placeholder="10-digit mobile number"
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={sending} block>
            <span className="fw-bold">Send code</span>
          </Button>
          <Button
            type="link"
            className="px-0 mt-2"
            onClick={() =>
              onBackToLogin(
                ((phoneForm.getFieldValue("phone") as string) ?? "").trim()
              )
            }
          >
            Back to login
          </Button>
        </Form>
      ) : (
        <Form
          key="verify-step"
          form={verifyForm}
          layout="vertical"
          onFinish={onSubmitPassword}
          disabled={submitting}
          requiredMark={false}
          className="w-75"
        >
          <div className="mb-3">
            We sent a code to <span className="fw-bold">{maskPhone(phone)}</span>.
          </div>

          {/* Password managers only offer to save a credential when an identifier
              field is present in the same form. The phone field lives on step 1
              and is unmounted here, so carry it hidden. */}
          <input
            type="text"
            name="username"
            value={phone}
            readOnly
            hidden
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
          />

          <Form.Item
            name="otp"
            label="Verification code"
            rules={[
              {
                required: true,
                pattern: /^\d{4,6}$/,
                message: "Enter the code we sent you",
              },
            ]}
          >
            <Input
              ref={otpRef}
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="New password"
            hasFeedback
            // Mirrors Django's MinimumLengthValidator to save a round trip. The
            // other three validators (common / numeric / similarity) stay
            // server-side and come back as field errors.
            rules={[
              { required: true, message: "Choose a password" },
              { min: 8, message: "Use at least 8 characters" },
            ]}
            extra="At least 8 characters. Avoid common words and anything close to your phone number."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm new password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              { required: true, message: "Re-enter the password" },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue("password") === value
                    ? Promise.resolve()
                    : Promise.reject(new Error("The two passwords don't match")),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={submitting} block>
            <span className="fw-bold">Set password and continue</span>
          </Button>

          <div className="d-flex justify-content-between mt-2">
            <Button
              type="link"
              className="px-0"
              disabled={cooldown > 0 || sending}
              loading={sending}
              onClick={() => sendCode(phone)}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </Button>
            <Button
              type="link"
              className="px-0"
              onClick={() => onBackToLogin(phone)}
            >
              Back to login
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};

export default SetPasswordForm;
